import { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, type FormatNumberOptions, type MessageValue, useIntl } from 'react-intl';
import { useReducedMotion } from 'react-native-reanimated';

import {
  checkIfCartCanShowUpsells,
  useBagFeaturedUpsellContent,
  useInitBagFeaturedUpsellMachine,
} from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { type useOrderState } from '../useOrderState';

// ─────────────────────────────────────────────────────────────────────────────

export const useBagFeaturedUpsell = (params: Params) => {
  const { orderState } = params;

  const {
    cart,
    isBagValid,
    isFetchingInitialData,
    isPollingPayment,
    isSubmittingOrder,
    isPerformingBagAction,
    isShowingModal,
    isShowingWarning,
    isAddingUpsellToBag,
    addUpsell,
  } = orderState;

  const { formatMessage, formatNumber } = useIntl();
  const isReduceMotionEnabled = useReducedMotion();

  const sessionInterval = useFeatureFlag('CELS-6114-order-bag-featured-upsell-show-every-n-sessions'); // prettier-ignore
  const content = useBagFeaturedUpsellContent({ pause: sessionInterval <= 0 });
  const [snapshot, send] = useInitBagFeaturedUpsellMachine({ addUpsell });

  // ─── Derived ───────────────────────────────────────────────────────────────

  const { product } = snapshot.context;

  const cartProductIds = useMemo(() => getCartProductIds(cart.lineItems), [cart.lineItems]);

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const isBagBusy = isFetchingInitialData || isPollingPayment || isSubmittingOrder || isPerformingBagAction; // prettier-ignore
  const isBagSettled = isBagValid && !isBagBusy && !isShowingModal && !isShowingWarning;
  const isProductInBag = Boolean(product && cartProductIds.includes(product.id));
  const isWaitingForBag = snapshot.matches('waiting-for-bag');
  const isAdding = snapshot.matches('adding');
  const isAdded = snapshot.matches('added');
  const isAnimating = snapshot.matches('animating');
  const isClosing = snapshot.matches('closing');
  const isClosingDuringConfetti = snapshot.matches({ animating: 'closing-dialog' });
  const isDialogContentVisible = snapshot.matches('visible') || isAdding || isAdded;
  const isDialogOpen = isDialogContentVisible || isClosing || isClosingDuringConfetti;
  const canNotifyBagReady = isWaitingForBag && isBagSettled && sessionInterval > 0;
  const hasAddFinished = isAdding && !isAddingUpsellToBag;

  // ───────────────────────────────────────────────────────────────────────────

  const price = product ? formatNumber(product.cost / 100, CURRENCY_FORMAT_OPTIONS) : '';
  const addCta = content?.addCta?.replaceAll('{price}', price) ?? formatMessage(messages.addCta, { price }); // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const notifyBagReady = useCallback(() => {
    if (!canNotifyBagReady || !content) {
      return;
    }

    send({
      type: 'BAG_READY',
      sessionInterval,
      restaurantId: cart.restaurantId,
      orderChannel: cart.orderChannel,
      productLineageKey: content.productLineageKey,
      hasAnimationImage: Boolean(content.animationImageUrl),
      canShowUpsells: checkIfCartCanShowUpsells(cart),
      cartProductIds,
      isReduceMotionEnabled,
    });
  }, [
    canNotifyBagReady,
    content,
    sessionInterval,
    cart,
    cartProductIds,
    isReduceMotionEnabled,
    send,
  ]);

  const addToBag = useCallback(() => {
    if (!isBagSettled) {
      return;
    }

    send({ type: 'ADD' });
  }, [isBagSettled, send]);

  const dismiss = useCallback(() => {
    send({ type: 'DISMISS' });
  }, [send]);

  const close = useCallback(() => {
    send({ type: 'CLOSE' });
  }, [send]);

  const notifyAddFinished = useCallback(() => {
    if (!hasAddFinished) {
      return;
    }

    send({ type: 'ADD_FINISHED', isProductInBag });
  }, [hasAddFinished, isProductInBag, send]);

  const finishAnimation = useCallback(() => {
    send({ type: 'ANIMATION_FINISHED' });
  }, [send]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(notifyBagReady, [notifyBagReady]);
  useEffect(notifyAddFinished, [notifyAddFinished]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isDialogOpen,
    isDialogContentVisible,
    isAnimating,
    isAdding,
    isAdded,
    content,
    addCta,
    addToBag,
    dismiss,
    close,
    finishAnimation,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCartProductIds(lineItems: readonly LineItem[]) {
  return lineItems.map(getLineItemProductId).filter(checkIfDefined);
}

function getLineItemProductId(lineItem: LineItem) {
  return lineItem.product?.id;
}

function checkIfDefined<Value>(value: Value | undefined): value is Value {
  return value !== undefined;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CURRENCY_FORMAT_OPTIONS: FormatNumberOptions = {
  style: 'currency',
  currency: 'USD',
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly addCta: { readonly price: MessageValue };
}>({
  addCta: {
    defaultMessage: 'Add for {price}',
    description: 'Bag | Featured upsell | Add CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  orderState: ReturnType<typeof useOrderState>;
};

type LineItem = Params['orderState']['cart']['lineItems'][number];
